import React, { useState, useCallback, useRef } from "react";
import { useSoundLibrary } from "../../soundLibrary";
import { InstrumentName } from "../../types/sound";
import { famousSongs, SongProgression } from "../../data/famousSongs";
import styles from "../../styles/SongProgressionDemo.module.css";

// Audio playback constants
const ARPEGGIO_SUSTAIN_MULTIPLIER = 1.5; // 50% overlap for richer sound
const MS_TO_SECONDS = 1000;

interface SongProgressionDemoProps {
    availableInstruments?: InstrumentName[];
}

export default function SongProgressionDemo({
    availableInstruments = ["piano", "e-piano", "saw-synth"]
}: SongProgressionDemoProps) {
    const { playChord, playNote, loadInstrument } = useSoundLibrary();

    const [selectedSong, setSelectedSong] = useState<SongProgression>(famousSongs[0]);
    const [selectedInstrument, setSelectedInstrument] = useState<InstrumentName>("piano");
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentChordIndex, setCurrentChordIndex] = useState<number | null>(null);
    const [showAnswer, setShowAnswer] = useState(false);
    const shouldStopRef = useRef(false);

    const playProgression = useCallback(async () => {
        if (isPlaying) {
            // Stop playback
            shouldStopRef.current = true;
            return;
        }

        setIsPlaying(true);
        setShowAnswer(false);
        shouldStopRef.current = false;

        // Ensure instrument is loaded
        await loadInstrument(selectedInstrument);

        // Play the progression twice
        for (let repeat = 0; repeat < 2; repeat++) {
            // Check if we should stop
            if (shouldStopRef.current) break;

            // Play each chord in sequence with proper timing
            for (let i = 0; i < selectedSong.chords.length; i++) {
                // Check if we should stop
                if (shouldStopRef.current) break;

                setCurrentChordIndex(i);
                const chord = selectedSong.chords[i];

                // Play based on the song's playback style
                if (selectedSong.playbackStyle === 'arpeggio' && selectedSong.arpeggioPattern) {
                    // Play arpeggio pattern
                    const pattern = selectedSong.arpeggioPattern;
                    const noteCount = pattern.length;
                    const noteDuration = selectedSong.chordDurationMs / noteCount;

                    for (let p = 0; p < noteCount; p++) {
                        if (shouldStopRef.current) break;

                        const noteIndex = pattern[p];
                        const note = chord.notes[noteIndex];

                        // Use longer sustain for richer piano sound (overlap notes)
                        await playNote(selectedInstrument, note, {
                            duration: (noteDuration * ARPEGGIO_SUSTAIN_MULTIPLIER) / MS_TO_SECONDS,
                            velocity: 0.75
                        });

                        // Wait before playing next note in arpeggio
                        await new Promise(resolve => setTimeout(resolve, noteDuration));
                    }
                } else {
                    // Play block chord
                    await playChord(selectedInstrument, chord.notes, {
                        duration: selectedSong.chordDurationMs / MS_TO_SECONDS,
                        velocity: 0.7
                    });

                    // Wait for the chord duration before playing next
                    await new Promise(resolve => setTimeout(resolve, selectedSong.chordDurationMs));
                }
            }
        }

        setCurrentChordIndex(null);
        setIsPlaying(false);
        shouldStopRef.current = false;
    }, [selectedSong, selectedInstrument, playChord, playNote, loadInstrument, isPlaying]);

    const handleSongChange = (songId: string) => {
        const song = famousSongs.find(s => s.id === songId);
        if (song) {
            setSelectedSong(song);
            setShowAnswer(false);
            setCurrentChordIndex(null);
        }
    };

    return (
        <div className={styles.demoContainer}>
            {/* Controls */}
            <div className={styles.controls}>
                <div className={styles.controlGroup}>
                    <label className={styles.label}>
                        Song:
                        <select
                            value={selectedSong.id}
                            onChange={(e) => handleSongChange(e.target.value)}
                            className={styles.select}
                            disabled={isPlaying}
                        >
                            {famousSongs.map(song => (
                                <option key={song.id} value={song.id}>
                                    {song.name} - {song.artist}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className={styles.controlGroup}>
                    <label className={styles.label}>
                        Instrument:
                        <select
                            value={selectedInstrument}
                            onChange={(e) => setSelectedInstrument(e.target.value as InstrumentName)}
                            className={styles.select}
                            disabled={isPlaying}
                        >
                            {availableInstruments.map(inst => (
                                <option key={inst} value={inst}>
                                    {inst}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            {/* Song Info */}
            <div className={styles.songInfo}>
                <div className={styles.songMeta}>
                    <span className={styles.tempo}>♩ = {selectedSong.bpm} BPM</span>
                    <span className={styles.key}>Key: {selectedSong.key}</span>
                </div>
                <p className={styles.description}>{selectedSong.description}</p>
            </div>

            {/* Chord Progression Visualization */}
            <div className={styles.progressionDisplay} aria-live="polite" aria-atomic="false">
                {selectedSong.chords.map((chord, index) => (
                    <div
                        key={`${selectedSong.id}-chord-${index}`}
                        className={`${styles.chordBox} ${
                            currentChordIndex === index ? styles.chordBoxActive : ''
                        } ${showAnswer ? styles.chordBoxRevealed : ''}`}
                        aria-current={currentChordIndex === index ? 'true' : 'false'}
                    >
                        <div className={styles.chordName}>{chord.name}</div>
                        {showAnswer && (
                            <div className={styles.chordNotes}>
                                {chord.notes.join(' - ')}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Play Button */}
            <div className={styles.playControls}>
                <button
                    onClick={playProgression}
                    className={styles.playButton}
                    aria-label={isPlaying ? 'Stop playback' : 'Play chord progression twice'}
                >
                    {isPlaying ? '⏹ Stop' : '▶ Play Progression (2x)'}
                </button>

                <button
                    onClick={() => setShowAnswer(!showAnswer)}
                    className={styles.showAnswerButton}
                    aria-label={showAnswer ? 'Hide individual note names' : 'Show individual note names'}
                >
                    {showAnswer ? 'Hide Notes' : 'Show Notes'}
                </button>
            </div>

            {/* Famous Songs List */}
            <div className={styles.famousSongs}>
                <h4 className={styles.famousSongsTitle}>This progression appears in:</h4>
                <ul className={styles.songsList}>
                    {selectedSong.famousSongs.map((song, index) => (
                        <li key={index} className={styles.songItem}>{song}</li>
                    ))}
                </ul>
                <p className={styles.moreInfo}>...and hundreds more!</p>
            </div>
        </div>
    );
}
